<template>
  <section class="screen-fleet-list" :class="{ 'is-compact': compact, 'is-product': kind === 'product', 'is-device': kind === 'device' }" :style="listStyle" :aria-label="heading" :data-fleet-node="index" :data-fleet-scope="requestKey">
    <header v-if="option.showTitle !== false || (option.showCount !== false && initialized && !awaitingPageProduct)" class="screen-fleet-list__header">
      <div class="screen-fleet-list__heading"><strong v-if="option.showTitle !== false" :title="heading">{{ heading }}</strong><span v-if="option.showCount !== false && !model.error && initialized">{{ total }} {{ kind === 'product' ? '个产品' : '台设备' }}</span></div>
    </header>
    <form v-if="option.showSearch" class="screen-fleet-list__search" role="search" @submit.prevent="search">
      <span class="screen-fleet-list__search-icon" aria-hidden="true"></span><input v-model="searchInput" data-fleet-control type="search" maxlength="100" :aria-label="heading + '名称或编号'" placeholder="搜索名称或编号">
      <button v-if="searchInput || keyword" data-fleet-control type="button" aria-label="清除搜索" @click="clearSearch">清除</button>
      <button data-fleet-control type="submit" :aria-label="'搜索' + heading">搜索</button>
    </form>
    <div ref="scroll" class="screen-fleet-list__scroll" tabindex="0" role="region" :aria-label="heading + '滚动区域'" :aria-busy="loading ? 'true' : 'false'" @scroll="onScroll">
      <div v-if="showPlaceholder" class="screen-fleet-list__placeholder">
        <span class="screen-fleet-list__sr-only" role="status">{{ awaitingPageProduct ? '等待当前页面产品' : '正在读取平台数据' }}</span>
        <ul class="screen-fleet-list__rows" aria-hidden="true">
          <li v-for="position in 3" :key="position" class="screen-fleet-list__row is-placeholder">
            <div class="screen-fleet-list__identity"><span class="screen-fleet-list__skeleton is-name"></span><span class="screen-fleet-list__skeleton is-id"></span></div>
            <div class="screen-fleet-list__placeholder-metrics"><span class="screen-fleet-list__skeleton"></span><span class="screen-fleet-list__skeleton"></span></div>
          </li>
        </ul>
      </div>
      <p v-else-if="model.error" class="screen-fleet-list__empty" role="alert">{{ model.error }}</p>
      <div v-else-if="loadError && !initialized" class="screen-fleet-list__empty" role="alert">{{ loadError }}<button type="button" class="screen-fleet-list__retry" data-fleet-retry @click="retry">重新加载</button></div>
      <p v-else-if="!model.rows.length" class="screen-fleet-list__empty" role="status">{{ keyword ? '没有匹配的' + (kind === 'product' ? '产品' : '设备') : model.empty }}<small>{{ emptyHelp }}</small></p>
      <ul v-else class="screen-fleet-list__rows">
        <li v-for="row in model.rows" :key="row.id" class="screen-fleet-list__row" :data-fleet-live-id="row.id"
            :class="{ 'is-navigable': rowNavigable(row) }" :role="rowNavigable(row) ? 'button' : null"
            :tabindex="rowNavigable(row) ? 0 : null" :aria-label="rowNavigable(row) ? '打开' + row.name : null"
            :data-fleet-row="rowNavigable(row) ? '' : null" :data-fleet-row-id="rowNavigable(row) ? row.id : null"
            :data-fleet-product-id="rowNavigable(row) ? (kind === 'product' ? row.id : row.productId) : null"
            @click="navigateRow(row)" @keydown.enter.prevent="navigateRow(row)" @keydown.space.prevent="navigateRow(row)">
          <div class="screen-fleet-list__identity"><span class="screen-fleet-list__name" :title="row.name">{{ row.name }}</span><small v-if="option.showId !== false" :title="row.id">编号：{{ row.id }}</small><small v-if="kind === 'device'" class="screen-fleet-list__product-name" :title="row.productName || row.productId">{{ row.productName || row.productId }}</small></div>
          <template v-if="kind === 'product'">
            <dl class="screen-fleet-list__product-metrics"><dt>设备总数</dt><dd>{{ count(row.deviceCount) }}<small>台</small></dd><dt>在线设备</dt><dd class="is-online">{{ count(row.onlineCount) }}<small>台</small></dd><template v-if="row.alarmCount !== undefined"><dt>告警设备</dt><dd :class="{ 'is-warning': row.alarmCount > 0 }">{{ count(row.alarmCount) }}<small>台</small></dd></template></dl>
          </template>
          <div v-else class="screen-fleet-list__metrics"><span class="screen-fleet-list__state" :class="'is-' + (row.state || 'unknown')"><i aria-hidden="true"></i>{{ stateText(row.state) }}</span><span class="screen-fleet-list__last-seen" :title="row.lastSeen || '上报时间未知'">{{ row.lastSeen || '上报时间未知' }}</span></div>
        </li>
      </ul>
      <p v-if="loading && initialized" class="screen-fleet-list__notice" role="status">{{ statsLoading ? '正在读取设备数量…' : showPagination ? '正在读取该页…' : '正在加载更多…' }}</p>
      <div v-if="loadError && initialized" class="screen-fleet-list__notice" role="alert">{{ loadError }}<button type="button" data-fleet-retry @click="retry">重试</button></div>
      <div v-if="liveError" class="screen-fleet-list__notice" role="status">{{ liveError }}<button type="button" data-fleet-retry @click="retryLive">重连</button></div>
      <div v-if="statsError" class="screen-fleet-list__notice" role="alert">部分设备数量暂不可用，未知数量显示为 —。<button type="button" data-fleet-retry @click="reload()">重新查询</button></div>
    </div>
    <footer v-if="showPagination && initialized" class="screen-fleet-list__pager" :aria-label="heading + '分页'">
      <span aria-live="polite">第 {{ visiblePageIndex + 1 }} / {{ pageCount }} 页 · 共 {{ total }} 条</span>
      <button data-fleet-control type="button" :disabled="loading || visiblePageIndex === 0" aria-label="上一页" @click="changePage(visiblePageIndex - 1)">上一页</button>
      <button data-fleet-control type="button" :disabled="loading || visiblePageIndex + 1 >= pageCount" aria-label="下一页" @click="changePage(visiblePageIndex + 1)">下一页</button>
    </footer>
  </section>
</template>
<script>
import props from './props'
import realtime from './realtime'
import { listState, deviceState, usesPlatformData, selectedProductId, validateListData } from './model'
import { fetchPlatformPage } from './platform'
import { listPageSize, listKeyword, filterListRows, nearListEnd, appendListRows } from './paging'
import { createRequestCancellation, isRequestCanceled } from '@/api/http'
import { fleetListRestore, restoreFleetListView } from '@/runtime/fleet-list-view'
import { productPageAction } from '@/runtime/page-navigation'
import { sharedReadOptions } from '@/runtime/shared-view'
import { fleetListLineColors, waitingForPageProduct } from './presentation'
export default {
  mixins: [props, realtime],
  inject: { contain: { default: () => ({}) } },
  props: { kind: { type: String, required: true } },
  data: () => ({ platformRows: [], loading: false, loadError: '',
    searchInput: '', keyword: '', pageIndex: 0, pageTotal: 0, hasMore: false, initialized: false, statsError: false, statsLoading: false }),
  computed: {
    heading () { return this.option.title || (this.kind === 'product' ? '产品列表' : '设备列表') },
    compact () { return Number(this.width) < 360 },
    hasRowNavigation () {
      return !this.contain.menuFlag && this.navigation && ['page', 'back', 'product'].includes(this.navigation.type)
    },
    platform () { return usesPlatformData(this.option) },
    awaitingPageProduct () { return waitingForPageProduct(this.kind, this.option, this.contain.navigationContext || {}, !!this.contain.menuFlag) },
    showPlaceholder () { return !this.model.error && (this.awaitingPageProduct || (this.loading && !this.initialized)) },
    showPagination () { return this.option.showPagination === true },
    pageSize () { return listPageSize(this.option) },
    requestKey () {
      return JSON.stringify([this.platform, this.kind, this.option.productScope, this.option.productIds,
        selectedProductId(this.option, this.contain.navigationContext || {}, !!this.contain.menuFlag),
        this.pageSize, this.showPagination, !!this.option.showSearch, this.contain.pageNavigation && this.contain.pageNavigation.pageId])
    },
    restoration () {
      if (this.contain.menuFlag || !this.contain.pageNavigation) return null
      return fleetListRestore(this.contain.pageNavigation.restore, this.index, this.requestKey)
    },
    filteredModel () {
      const model = listState(this.kind, this.platform ? this.platformRows : this.data, this.option, this.contain.navigationContext || {}, !!this.contain.menuFlag)
      return this.platform ? model : { ...model, rows: filterListRows(model.rows, this.keyword) }
    },
    total () { return this.platform ? this.pageTotal : this.filteredModel.rows.length },
    pageCount () { return Math.max(1, Math.ceil(this.total / this.pageSize)) },
    visiblePageIndex () { return Math.min(this.pageIndex, this.pageCount - 1) },
    model () {
      const model = this.filteredModel
      const start = this.visiblePageIndex * this.pageSize
      return !this.platform && this.showPagination ? { ...model, rows: model.rows.slice(start, start + this.pageSize) } : model
    },
    emptyHelp () {
      return this.option.productScope === 'current' && !this.contain.menuFlag
        ? '需要由页面导航传入 productId' : this.platform ? '' : '请调整产品范围或静态数据'
    },
    listStyle () {
      const lines = fleetListLineColors(this.option)
      return {
        '--fleet-row-height': (this.option.rowHeight || 68) + 'px', fontSize: (this.option.fontSize || 16) + 'px',
        '--fleet-secondary': this.option.secondaryColor || '#97b8c8', '--fleet-online': this.option.onlineColor || '#5ee5b0',
        '--fleet-offline': this.option.offlineColor || '#ffb76b', '--fleet-border': lines.borderColor, '--fleet-separator': lines.separatorColor,
        color: this.option.color || '#d8f4ff', backgroundColor: this.option.backgroundColor || '#0b202d'
      }
    }
  },
  watch: {
    requestKey: { immediate: true, handler () { this.reload(true) } },
    model: { deep: true, handler () { this.syncView() } },
    option: { deep: true, handler () { this.syncView() } },
    restoration (entry) { if (entry && entry !== this._cacheEntry) this.reload(true); else this.syncView() },
    'contain.pageNavigation' () { this.syncView(true) },
    loading () { this.syncView() }
  },
  mounted () {
    this.$el.captureFleetView = () => this.captureView()
    if (typeof ResizeObserver !== 'undefined') { this._resizeObserver = new ResizeObserver(() => this.onScroll()); this._resizeObserver.observe(this.$el) }
    this.syncView(true)
  },
  beforeDestroy () {
    this._disposed = true
    delete this.$el.captureFleetView
    if (this._request) this._request.cancel()
    if (this._resizeObserver) this._resizeObserver.disconnect()
  },
  methods: {
    syncView (reset = false) {
      this.$nextTick(() => {
        if (this._disposed) return
        // 等待列表及分页布局更新后再恢复位置。
        this.$nextTick(() => {
          if (this._disposed || !this.$refs.scroll) return
          const entry = this.restoration
          if (!entry) {
            if (reset) this.$refs.scroll.scrollTop = 0
          } else if (this._restoredEntry !== entry && !this.loading && !this.loadError && !this.model.error) {
            const origin = this.contain.pageNavigation.restore
            const rowId = String(origin.nodeId) === String(this.index) ? origin.rowId : undefined
            const container = this.contain.$refs && this.contain.$refs.container
            restoreFleetListView(this.$el, entry, rowId, container && container.$refs.container)
            this._restoredEntry = entry
          }
          this.maybeLoadMore()
        })
      })
    },
    reload (restore = false) {
      if (this.releaseLive) this.releaseLive()
      this._generation = (this._generation || 0) + 1
      if (this._request) this._request.cancel()
      this.platformRows = []; this.loadError = ''; this.loading = false; this.statsError = false; this.statsLoading = false
      this.initialized = false; this.pageIndex = 0; this.pageTotal = 0; this.hasMore = false
      this._failedPage = null
      const entry = restore === true ? this.restoration : null
      const cached = entry && entry.list
      this._cacheEntry = entry
      if (restore === true) { this.keyword = ''; this.searchInput = ''; this._restoredEntry = null }
      // 页面联动参数尚未到达时保留列表结构，不查全部设备，也不恢复其他产品缓存。
      if (this.awaitingPageProduct) { this.syncView(true); return Promise.resolve() }
      if (cached && cached.version === 1 && cached.platform === this.platform &&
        Number.isSafeInteger(cached.pageIndex) && cached.pageIndex >= 0 &&
        typeof cached.keyword === 'string' && cached.keyword.length <= 100 &&
        Number.isSafeInteger(cached.total) && cached.total >= 0 && Array.isArray(cached.rows) &&
        cached.rows.length <= this.pageSize * (cached.pageIndex + 1) &&
        !validateListData(this.kind, cached.rows, Infinity)) {
        this.keyword = cached.keyword
        this.searchInput = typeof cached.searchInput === 'string' ? cached.searchInput.slice(0, 100) : cached.keyword
        this.platformRows = cached.rows.map(row => ({ ...row })); this.pageIndex = cached.pageIndex
        this.pageTotal = cached.total; this.hasMore = !!cached.hasMore; this.statsError = !!cached.statsError
        this.initialized = true
        this.syncView(true)
        return Promise.resolve()
      }
      if (this.$refs.scroll) this.$refs.scroll.scrollTop = 0
      this.syncView(true)
      if (!this.platform) { this.initialized = true; return Promise.resolve() }
      return this.loadPage(0)
    },
    async loadPage (pageIndex, append = false) {
      if (this.loading || this._disposed || this.awaitingPageProduct) return
      if (!append && this.releaseLive) this.releaseLive()
      const generation = this._generation
      this._request = createRequestCancellation()
      this.loading = true; this.loadError = ''
      this._failedPage = { pageIndex, append }
      const previousRows = this.platformRows
      const commit = page => {
        if (this._disposed || generation !== this._generation) return
        // 加载更多期间旧行仍在实时更新；按原有ID重取当前值，不能用请求开始时的旧快照覆盖。
        const previousIds = new Set(previousRows.map(row => row.id))
        const currentRows = append ? this.platformRows.filter(row => previousIds.has(row.id)) : []
        const rows = append ? appendListRows(currentRows, page.rows) : page.rows
        if (append && page.hasMore && rows.length === currentRows.length) throw new Error('平台返回了重复批次，请重试')
        this.platformRows = rows
        this.pageTotal = page.total; this.pageIndex = page.pageIndex; this.hasMore = page.hasMore
        this.statsError = append ? this.statsError || page.statsError : page.statsError
        this.initialized = true
        if (!append && this.$refs.scroll) this.$refs.scroll.scrollTop = 0
      }
      try {
        const page = await fetchPlatformPage(this.kind, this.option, this.contain.navigationContext || {}, !!this.contain.menuFlag,
          pageIndex, this.keyword, sharedReadOptions(this.contain, { cancelToken: this._request.token }), page => {
            if (this._disposed || generation !== this._generation) return
            commit(page); this.statsLoading = true
          }, !this.canObserveFleet)
        if (this._disposed || generation !== this._generation) return
        commit(page); this._failedPage = null
      } catch (error) {
        if (!this._disposed && generation === this._generation && !isRequestCanceled(error)) this.loadError = error.message || '平台数据读取失败'
      } finally {
        if (!this._disposed && generation === this._generation) { this.loading = false; this.statsLoading = false; this.syncView() }
      }
    },
    retry () { if (!this.loading && this._failedPage) return this.loadPage(this._failedPage.pageIndex, this._failedPage.append) },
    search () {
      this.keyword = listKeyword(this.searchInput); this.searchInput = this.keyword
      return this.reload()
    },
    clearSearch () { this.searchInput = ''; return this.search() },
    changePage (pageIndex) {
      if (this.loading || pageIndex < 0 || pageIndex >= this.pageCount) return
      if (this.platform) return this.loadPage(pageIndex)
      this.pageIndex = pageIndex
      if (this.$refs.scroll) this.$refs.scroll.scrollTop = 0
      this.syncView()
    },
    onScroll () { this.maybeLoadMore(); if (this.scheduleLive) this.scheduleLive() },
    maybeLoadMore () {
      if (this.platform && !this.showPagination && this.initialized && this.hasMore && !this.loading && !this.loadError &&
        (!this.restoration || this._restoredEntry === this.restoration) && nearListEnd(this.$refs.scroll, this.option.rowHeight || 68)) {
        this.loadPage(this.pageIndex + 1, true)
      }
    },
    captureView () {
      if (!this.initialized) return null
      return { version: 1, platform: this.platform, keyword: this.keyword, searchInput: this.searchInput,
        pageIndex: this.pageIndex, total: this.total, hasMore: this.hasMore, statsError: this.statsError || this.statsLoading,
        rows: this.platform ? this.platformRows.map(row => ({ ...row })) : [] }
    },
    stateText: deviceState,
    rowNavigable (row) {
      if (!this.hasRowNavigation) return false
      return this.navigation.type !== 'product' || productPageAction(this.navigation, row, this.kind).type !== 'none'
    },
    navigateRow (row) {
      if (!this.rowNavigable(row) || !this.contain.runPageNavigation) return
      this.contain.runPageNavigation({
        index: this.index,
        component: { prop: this.kind === 'product' ? 'productList' : 'deviceList' },
        navigation: this.navigation,
        data: { row }
      })
    },
    count (value) { return value === undefined ? '—' : value }
  }
}
</script>
<style scoped>
.screen-fleet-list { width: 100%; height: 100%; min-width: 0; box-sizing: border-box; display: flex; flex-direction: column; border: 1px solid var(--fleet-border); border-radius: 6px; padding: 14px 18px 8px; overflow: hidden; font-family: inherit; }
.screen-fleet-list__header { flex: none; min-width: 0; padding-bottom: 12px; border-bottom: 1px solid var(--fleet-separator); }
.screen-fleet-list__heading { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 12px; min-width: 0; }
.screen-fleet-list__heading strong { font-size: 1em; font-weight: 600; text-overflow: ellipsis; overflow: hidden; white-space: nowrap; min-width: 0; }
.screen-fleet-list__heading span { color: var(--fleet-secondary); font-size: .8em; margin-left: auto; }
.screen-fleet-list__scroll { flex: 1; min-height: 0; min-width: 0; overflow: auto; scrollbar-width: none; -ms-overflow-style: none; overscroll-behavior: contain; }
.screen-fleet-list__scroll::-webkit-scrollbar { display: none; }
.screen-fleet-list__scroll:focus-visible { outline: 2px solid var(--fleet-online); outline-offset: -2px; }
.screen-fleet-list__rows { margin: 0; padding: 0; list-style: none; }
.screen-fleet-list__row { display: flex; align-items: center; gap: 12px; min-width: 0; min-height: var(--fleet-row-height); box-sizing: border-box; padding: 14px 4px; border-bottom: 1px solid var(--fleet-separator); }
.is-product .screen-fleet-list__row { display: grid; grid-template-columns: minmax(0, 1fr) minmax(128px, .9fr); gap: 12px 20px; }
.screen-fleet-list__row:last-child { border-bottom: 0; }
.screen-fleet-list__row.is-navigable { cursor: pointer; }
.screen-fleet-list__row.is-navigable:hover, .screen-fleet-list__row.is-navigable:focus-visible { background: rgba(80, 198, 222, .08); box-shadow: inset 2px 0 var(--fleet-online); outline: 1px solid var(--fleet-separator); outline-offset: -1px; }
.screen-fleet-list__identity { flex: 1; min-width: 0; }
.screen-fleet-list__name { display: block; line-height: 1.5; font-weight: 600; text-overflow: ellipsis; overflow: hidden; white-space: nowrap; }
.screen-fleet-list__identity small { display: block; margin-top: 4px; color: var(--fleet-secondary); font-size: .75em; line-height: 1.4; text-overflow: ellipsis; overflow: hidden; white-space: nowrap; }
.screen-fleet-list__identity .screen-fleet-list__product-name { margin-top: 3px; }
.screen-fleet-list__metrics { display: flex; flex-direction: column; min-width: 0; gap: 8px; align-items: flex-end; flex-shrink: 0; max-width: 40%; font-size: .8em; color: var(--fleet-secondary); text-align: right; }
.screen-fleet-list__last-seen { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: .9em; }
.screen-fleet-list__product-metrics { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: baseline; gap: 7px 10px; min-width: 0; margin: 0; padding-left: 16px; border-left: 1px solid var(--fleet-separator); font-size: .8em; }
.screen-fleet-list__product-metrics dt { color: var(--fleet-secondary); min-width: 0; overflow-wrap: anywhere; }
.screen-fleet-list__product-metrics dd { margin: 0; text-align: right; font-weight: 600; font-size: 1.2em; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
.screen-fleet-list__product-metrics dd small { margin-left: 5px; font-size: .7em; font-weight: 400; }
.screen-fleet-list__product-metrics .is-warning { color: var(--fleet-offline); }
.screen-fleet-list__product-metrics .is-online { color: var(--fleet-online); }
.screen-fleet-list__state { display: inline-flex; align-items: center; gap: 7px; white-space: nowrap; }
.screen-fleet-list__state i { display: inline-block; width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.screen-fleet-list__state.is-online { color: var(--fleet-online); }
.screen-fleet-list__state.is-offline { color: var(--fleet-offline); }
.screen-fleet-list__empty { margin: 0; padding: 22px 0; line-height: 1.6; overflow-wrap: anywhere; text-align: center; }
.screen-fleet-list__empty small { display: block; margin-top: 8px; color: var(--fleet-secondary); font-size: .8em; }
.screen-fleet-list__retry { display: block; margin: 10px auto 0; padding: 6px 12px; border: 1px solid var(--fleet-separator); border-radius: 4px; color: inherit; background: transparent; cursor: pointer; }
.screen-fleet-list__search { position: relative; flex: none; display: flex; gap: 8px; margin: 12px 0 6px; min-width: 0; }
.screen-fleet-list__search-icon { position: absolute; left: 12px; top: 50%; width: 10px; height: 10px; border: 1.5px solid var(--fleet-secondary); border-radius: 50%; transform: translateY(-65%); pointer-events: none; }
.screen-fleet-list__search-icon::after { content: ''; position: absolute; right: -4px; bottom: -3px; width: 5px; height: 1.5px; background: var(--fleet-secondary); transform: rotate(45deg); }
.screen-fleet-list__search input { flex: 1; width: 0; min-width: 0; padding: 9px 10px 9px 33px; border: 1px solid var(--fleet-separator); border-radius: 4px; color: inherit; background: rgba(32, 110, 150, .06); font: inherit; font-size: .8em; }
.screen-fleet-list__search input::placeholder { color: var(--fleet-secondary); opacity: 1; }
.screen-fleet-list button { border: 1px solid var(--fleet-separator); border-radius: 4px; color: inherit; background: transparent; padding: 6px 8px; font: inherit; font-size: .8em; cursor: pointer; }
.screen-fleet-list button:hover:not(:disabled) { background: rgba(80, 198, 222, .12); }
.screen-fleet-list button:disabled { opacity: .5; cursor: default; }
.screen-fleet-list button:focus-visible, .screen-fleet-list input:focus-visible { outline: 2px solid var(--fleet-online); outline-offset: -2px; }
.screen-fleet-list__pager { flex: none; display: flex; justify-content: flex-end; align-items: center; flex-wrap: wrap; gap: 6px; padding-top: 8px; border-top: 1px solid var(--fleet-separator); }
.screen-fleet-list__pager span { margin-right: auto; color: var(--fleet-secondary); font-size: .75em; }
.screen-fleet-list__notice { margin: 0; padding: 12px 0; font-size: .8em; line-height: 1.6; color: var(--fleet-secondary); overflow-wrap: anywhere; text-align: center; }
.screen-fleet-list__notice button { margin-left: 8px; }
.screen-fleet-list__skeleton { display: block; height: .75em; border-radius: 2px; background: var(--fleet-secondary); opacity: .12; }
.screen-fleet-list__skeleton.is-name { width: 70%; height: 1em; }
.screen-fleet-list__skeleton.is-id { width: 52%; margin-top: 10px; height: .65em; }
.screen-fleet-list__placeholder-metrics { display: flex; flex-direction: column; gap: 12px; width: 90px; max-width: 100%; margin-left: auto; }
.screen-fleet-list__placeholder-metrics .screen-fleet-list__skeleton:last-child { width: 72%; margin-left: auto; }
.screen-fleet-list__sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
@media (pointer: coarse) { .screen-fleet-list button, .screen-fleet-list input { min-height: 44px; } }
.is-compact { padding: 10px; }
.is-compact .screen-fleet-list__row { gap: 10px; padding: 12px 2px; }
.is-compact.is-product .screen-fleet-list__row { grid-template-columns: minmax(0, 1fr); gap: 12px; }
.is-compact .screen-fleet-list__product-metrics { border-left: 0; border-top: 1px solid var(--fleet-separator); padding: 10px 0 0; }
.is-compact .screen-fleet-list__placeholder-metrics { width: 100%; }
.is-compact.is-device .screen-fleet-list__placeholder-metrics { width: 56px; }
</style>
